import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Table from "../../../components/table/table";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import TableHeaderTextField from "../../../components/table-header-text-filed/table-header-text-filed";
import { useDispatch, useSelector } from "react-redux";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { getAllFormTemplatesRequest } from "../../../redux/form-templates/slice";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import TableHeaderDatePicker from "../../../components/table-header-datepicker/table-header-datepicker";
import TableHeaderSelectField from "../../../components/table-header-select-field/table-header-select-field";
import TableHeaderMultiSelectField from "../../../components/table-header-multi-select-field/table-header-multi-select-field";
import {
  getCertificationTypesRequest,
  getInspectionServicesRequest,
  getInsuranceTypesRequest,
} from "../../../redux/inspection-firms/slice";
import AddTemplate from "./add-template/add-template";
import { useNavigate } from "react-router-dom";
import {
  duplicateFormTemplateRequest,
  deleteFormTemplatesRequest,
} from "../../../utils/APICalls";
import Loading from "../../../components/loading/loading";
import Notification from "../../../components/notification/notification";
import ErrorNotification from "../../../components/error-notification/error-notification";
import ContactSpectrum from "../../../components/contact-spectrum/constact-spectrum";

dayjs.extend(utc);

const NameRenderer = ({ rowData }: any) => {
  const navigate = useNavigate();
  const name = encodeURIComponent(rowData?.displayName);
  const onFormNameClick = () => {
    navigate(`/form-templates/${name}`, {
      state: rowData,
    });
  };
  return (
    <Box>
      <Button sx={{ textTransform: "none" }} onClick={onFormNameClick}>
        <Typography>{rowData.displayName}</Typography>
      </Button>
    </Box>
  );
};

const RestrictionsRenderer = ({ value }: any) => {
  const hasBackgroundCheck = value.hasOwnProperty("backgroundCheckDone");
  const hasCertifications =
    value?.certifications && value?.certifications?.length > 0;
  const hasInsurances = value?.insurances && value?.insurances?.length > 0;

  const itemCount =
    (hasBackgroundCheck ? 1 : 0) +
    (hasCertifications ? 1 : 0) +
    (hasInsurances ? 1 : 0);

  const isSingleItem = itemCount === 1;

  return (
    <Box
      sx={{
        overflowX: "auto",
        scrollbarWidth: "none",
        display: "flex",
        flexDirection: "column",
        justifyContent: isSingleItem ? "center" : "flex-start",
        minHeight: isSingleItem ? "100%" : "auto",
      }}
    >
      {hasBackgroundCheck && (
        <Typography display={"flex"} alignItems={"center"} fontSize={14}>
          Background Check Completed-
          <Chip
            sx={{ ml: 0.5 }}
            size="small"
            color="primary"
            variant="outlined"
            label={value.backgroundCheckDone ? "True" : "False"}
          />
        </Typography>
      )}
      {hasCertifications && (
        <Typography
          display={"flex"}
          alignItems={"center"}
          fontSize={14}
          mt={0.25}
        >
          Certifications-
          {value?.certifications.map((certification: string) => (
            <Chip
              key={certification}
              sx={{ ml: 0.5 }}
              size="small"
              color="primary"
              variant="outlined"
              label={certification}
            />
          ))}
        </Typography>
      )}
      {hasInsurances && (
        <Typography
          display={"flex"}
          alignItems={"center"}
          fontSize={14}
          mt={0.25}
        >
          Insurances-
          {value?.insurances.map((insurance: string) => (
            <Chip
              key={insurance}
              sx={{ ml: 0.5 }}
              size="small"
              color="primary"
              variant="outlined"
              label={insurance}
            />
          ))}
        </Typography>
      )}
    </Box>
  );
};

export default function FormTemplates() {
  const dispatch = useDispatch();

  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [filterParams, setFilterParams] = useState<any>({});
  const [category, setCategory] = useState<any[]>([]);
  const [type, setType] = useState<any[]>([]);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { loading, allFormTemplates } = useSelector(
    (state: any) => state.formTemplates
  );
  const { insuranceTypes, inspectionServices, certificationTypes } =
    useSelector((state: any) => state.inspectionFirms);

  const rowCountRef = useRef(allFormTemplates?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (allFormTemplates?.totalCount !== undefined) {
      rowCountRef.current = allFormTemplates?.totalCount;
    }
    return rowCountRef.current;
  }, [allFormTemplates?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "displayName",
      headerName: "Form Name",
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <NameRenderer rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="name"
          sortField="name"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
    {
      field: "inspectionTypeCategory",
      headerName: "Category",
      width: 180,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="category"
          sortField="category"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
          values={category}
        />
      ),
    },
    {
      field: "inspectionType",
      headerName: "Type",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) => {
        const findType = inspectionServices.find(
          (t: any) => t?.inspectionType === value
        );
        return findType ? findType?.displayName : "-";
      },
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="type"
          sortField="type"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
          values={type}
        />
      ),
    },
    {
      field: "clientFee",
      headerName: "Client Fee",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) => (value ? `$${value}` : " "),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="clientFee"
          sortField="client_fee"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
    {
      field: "inspectorFee",
      headerName: "Inspector Fee",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) => (value ? `$${value}` : " "),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="inspectorFee"
          sortField="inspector_fee"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
    {
      field: "restrictions",
      headerName: "Restrictions",
      minWidth: 280,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <RestrictionsRenderer value={params.value} />
      ),
      renderHeader: (params) => (
        <TableHeaderMultiSelectField
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
          values={{
            insuranceTypes:
              Array.from(insuranceTypes, (obj: any) => obj.insuranceType) || [],
            certificateTypes:
              Array.from(
                certificationTypes,
                (obj: any) => obj.certificationType
              ) || [],
          }}
        />
      ),
    },
    {
      field: "updatedDateTime",
      headerName: "Last Updated",
      width: 200,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY, h:mma") : " ",
      renderHeader: (params) => (
        <TableHeaderDatePicker
          field="lastUpdated"
          sortField="last_updated"
          headerName={params.colDef.headerName}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });

    if (inspectionServices.length === 0)
      dispatch(getInspectionServicesRequest());
    if (insuranceTypes.length === 0) dispatch(getInsuranceTypesRequest());
    if (certificationTypes.length === 0)
      dispatch(getCertificationTypesRequest());
  }, []); // eslint-disable-line

  useEffect(() => {
    if (inspectionServices && inspectionServices.length > 0) {
      const uniqueCategories = inspectionServices
        .reduce((acc: any[], item: any) => {
          if (!acc.includes(item.category)) {
            acc.push(item.category);
          }
          return acc;
        }, [])
        .sort((a: string, b: string) => a.localeCompare(b));
      setCategory(uniqueCategories);

      const inspectionType = inspectionServices.map((service: any) => ({
        value: service?.inspectionType,
        label: service?.displayName,
      }));
      setType(inspectionType);
    }
  }, [inspectionServices]);

  useEffect(() => {
    setRowData(allFormTemplates?.items);
  }, [allFormTemplates]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterForm = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else if (keys.includes("backgroundCheckCompleted")) {
      // handle form template restrictions filter
      if (data.backgroundCheckCompleted === false) {
        const { backgroundCheckCompleted, ...restrinctionFilter } = data;
        if (filterParams.hasOwnProperty("backgroundCheckCompleted")) {
          delete filterParams["backgroundCheckCompleted"];
        }
        setFilterParams({ ...filterParams, ...restrinctionFilter });
      } else {
        setFilterParams({ ...filterParams, ...data });
      }
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      dispatch(getAllFormTemplatesRequest(filters));
    }
  }, [filterParams]); // eslint-disable-line

  const handleDuplicateTemplate = async () => {
    if (rowSelectionModel.length !== 1) return;

    setLoading2(true);
    try {
      const response = await duplicateFormTemplateRequest(
        rowSelectionModel[0] as string
      );

      if (response?.status === 200 || response?.status === 201) {
        setNotificationMessage("Form template duplicated successfully.");
        setShowNotification(true);
        setRowSelectionModel([]);
        if (Object.keys(filterParams).length > 0) {
          dispatch(getAllFormTemplatesRequest(filterParams));
        }
      } else {
        setErrorMessage(
          response?.data?.message ||
            "Failed to duplicate form template. Please try again."
        );
        setShowErrorNotification(true);
      }
    } catch (error) {
      setErrorMessage("Failed to duplicate form template. Please try again.");
      setShowErrorNotification(true);
    } finally {
      setLoading2(false);
    }
  };

  const handleDeleteTemplates = async () => {
    if (rowSelectionModel.length === 0) return;

    setLoading2(true);
    try {
      const response = await deleteFormTemplatesRequest(
        rowSelectionModel as string[]
      );

      if (response?.status === 200 || response?.status === 204) {
        setNotificationMessage(
          `${
            rowSelectionModel.length > 1 ? "Form templates" : "Form template"
          } deleted successfully.`
        );
        setShowNotification(true);
        setRowSelectionModel([]);
        if (Object.keys(filterParams).length > 0) {
          dispatch(getAllFormTemplatesRequest(filterParams));
        }
      } else {
        setErrorMessage(
          response?.data?.message ||
            "Failed to delete form template(s). Please try again."
        );
        setShowErrorNotification(true);
      }
    } catch (error) {
      setErrorMessage("Failed to delete form template(s). Please try again.");
      setShowErrorNotification(true);
    } finally {
      setLoading2(false);
    }
  };

  return (
    <>
      <ContactSpectrum />
      <Breadcrumb />
      <Typography sx={{ ml: 2 }} className="title">
        Form Templates
      </Typography>
      <Box
        sx={{ mt: 3 }}
        className="clients-header"
        justifyContent={"flex-end"}
      >
        <Button
          id="add-button"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddTemplate(true)}
        >
          Add Template
        </Button>
      </Box>

      <Box
        className="clients-header"
        sx={{
          my: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          {rowSelectionModel.length > 0 && (
            <>
              <Button
                disabled={rowSelectionModel.length > 1}
                onClick={handleDuplicateTemplate}
              >
                Duplicate
              </Button>
              <Divider orientation="vertical" sx={{ mx: 0.5, height: 34 }} />
              <IconButton aria-label="delete" onClick={handleDeleteTemplates}>
                <DeleteOutlinedIcon color="error" />
              </IconButton>
            </>
          )}
        </Box>
        <Box>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          rowData={rowData}
          rowCount={rowCount}
          loading={loading}
          columnDef={column}
          headerHeight={headerHeight}
          disableColumnMenu={true}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          rowHeight={90}
          disableColumnResize={false}
        />
      </Box>

      {showAddTemplate && (
        <AddTemplate
          showDialog={showAddTemplate}
          closeDialog={() => setShowAddTemplate(false)}
        />
      )}

      <Notification
        show={showNotification}
        message={notificationMessage}
        onClose={() => setShowNotification(false)}
      />

      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />

      {loading2 && <Loading />}
    </>
  );
}
